import { storageProxy } from '@/utils/cache'
import { CacheKeys } from '@/enums/cacheEnum'
import { LocaleEnum } from '@/enums/localeEnum'
import { LocaleSettingsType, localeSettings, LocaleType } from '@/settings/locale'
import { isObject } from '@/utils/is'
import { set } from '@/utils'
import { changeDayjsLocale } from 'fbu-antdv-components'
import { asyncScript } from '@/utils/dom'
import dayjs from 'dayjs'

/**
 * 获取locale本地缓存
 * @returns
 */
export function getLocaleInfoCache(isTransform = true): LocaleSettingsType {
  const cacheLocale = storageProxy.get(CacheKeys.LOCALE)
  if (cacheLocale && isObject(cacheLocale)) {
    if (cacheLocale.locale && isTransform) {
      cacheLocale.locale = transformLocale(cacheLocale.locale)
    }
    return cacheLocale
  }
  const retInfo = { ...localeSettings }
  const language = transformLocale(navigator.language.replace('-', '_')) as LocaleType

  const values = Object.values(LocaleEnum)

  if (values.includes(language)) {
    retInfo.locale = language
    retInfo.originalLocale = language
  }

  return retInfo
}

/**
 * 创建消息Object，以文件名作为模块名字
 * @param modulesMap 语言包文件映射对象，如{./zh-CN/routes.ts: {}, ./zh-CN/xxxxx/test.ts: {}}
 * @param localePrefix 语言包文件夹名字
 * @returns Object
 */
export const createMessageByModules = (
  modulesMap: Record<string, Record<string, any>>,
  localePrefix = 'zh-CN',
) => {
  const ret: Recordable = {}
  Object.keys(modulesMap).forEach((mPath) => {
    const module = modulesMap[mPath].default

    const fullFileName = mPath.replace(`./${localePrefix}/`, '')
    const lastIndex = fullFileName.lastIndexOf('.')
    const fileName = fullFileName.substring(0, lastIndex)
    const keyList = fileName.split('/')
    const moduleName = keyList.shift()
    const objKey = keyList.join('.')

    if (moduleName) {
      if (objKey) {
        set(ret, moduleName, ret[moduleName] || {})
        set(ret[moduleName], objKey, module)
      } else {
        set(ret, moduleName, module || {})
      }
    }
  })
  return ret
}

/**
 * 缓存使用过的语言
 */
export const loadedLocale: LocaleType[] = []
export function setLoadedLocale(locale: LocaleType) {
  if (loadedLocale.includes(locale) !== true) {
    loadedLocale.push(locale)
  }
}

// export async function loadTranslateJson() {
//   let message: Recordable = {}
//   try {
//     const result = await getI18nList()
//     message = result.records.reduce((acc, item) => {
//       acc[item.codeStr] = item.codeValue
//       return acc
//     }, message)
//   } catch (err: any) {
//     console.log('Load i18n translation occured error: ', err.message)
//   }
//   return { message }
// }

export function loadTranslateJson(locale: LocaleType): Promise<Recordable> {
  return new Promise((resolve, reject) => {
    const id = 'translate-script'
    // const langInfo = storageProxy.get(CacheKeys.LOCALE)
    const lang = locale
    const url = `/api/base/web/language/all/label/file.js?v=${dayjs().format(
      'YYYYMMDD',
    )}&currentLangCode=${lang}`
    const key = `myData`
    const printScriptNode = document.getElementById(id)
    if (printScriptNode) {
      return resolve({
        message: kvToMap(window[key] ? window[key][lang] : []),
      })
    }
    asyncScript(url)
      .then((scriptNode) => {
        scriptNode.id = id
        resolve({
          message: kvToMap(window[key] ? window[key][lang] : []),
        })
      })
      .catch((error) => {
        reject(error)
      })
  })
}

const kvToMap = (val: { k: string; v: string }[]) => {
  return val.reduce((pre: { [key: string]: string }, val) => {
    pre[val.k] = val.v
    return pre
  }, {})
}

/**
 * 通过locale获取翻译数据
 * @param locale 语言
 * @returns
 */
export async function importLocaleData(locale: LocaleType): Promise<Recordable | null> {
  if (locale.trim() === '') {
    return null
  }
  let result: Recordable = {}
  try {
    const localTranslate = await import(`./lang/${locale}.ts`)
    let translateData: Recordable = {}
    if (window.top !== window.self) {
      translateData = {
        message: window.top?.contextBridge.getLocaleMessage(),
      }
    } else {
      translateData = await loadTranslateJson(locale)
    }
    result = { ...localTranslate }
    Object.assign(result.default.message, translateData.message)
    changeDayjsLocale(localTranslate.default.momentLocale)
  } catch (_) {
    console.error(_)
    result = {}
  }
  return result
}

/**
 * 设置HTML元素lang属性
 * @param locale
 */
export function setHTMLElemLang(locale: LocaleType) {
  document.querySelector('html')?.setAttribute('lang', locale)
}

/**
 * 转换语言简写为国际标准
 * @param locale 语言简写
 */
export function transformLocale(lang: string): string {
  const langMap = {
    zh_CN: LocaleEnum.CN,
    en_US: LocaleEnum.US,
    jp_JP: LocaleEnum.JP,
    zh_tw: LocaleEnum.TW,
    ru_RU: LocaleEnum.RU,
    fr: LocaleEnum.FR,
    sn_US: LocaleEnum.SN_US,
    cz_CZ: LocaleEnum.CZ,
    ro_RO: LocaleEnum.RO,
    de_DE: LocaleEnum.DE,
  }
  return langMap[lang] || lang
}
